import ENDPOINTS from '@/config/http/endpoints'
import { parseResponse } from '@/config/http/http.helpers'
import {
  mapGetOrdersResponse,
  mapOrderItem
} from '@/ducks/orders/helpers/orders.helpers'
import {
  ApiAcceptOrder,
  ApiAcceptOrderSchema,
  ApiFetchOrderBillingsResponse,
  ApiFetchOrderBillingsResponseSchema,
  ApiFetchOrderDetailsResponse,
  ApiFetchOrderDetailsResponseSchema,
  ApiFetchRejectionReasons,
  ApiFetchRejectionReasonsSchema,
  ApiSearchOrdersHistoryResponse
} from '@/ducks/orders/orders.types'
import {
  GetOrdersDTO,
  GetOrdersHistoryDTO,
  GetOrdersResponse,
  OrderItemAPI
} from '@/ducks/orders/services/orders.api.types'
import { OrderItem } from '@/ducks/orders/slices/orders.slice'
import { rootApi } from '@/ducks/root.api'
import { ApiBaseResponse, ApiBaseResponseSchema } from '@/ducks/slice.types'

const HISTORY_STATUSES = [
  'REJECTED',
  'CANCELED',
  'RETURNED',
  'DELIVERED',
  'PICKED_UP'
]

export const ordersApi = rootApi.injectEndpoints({
  endpoints: build => {
    return {
      fetchOrdersList: build.query<
        { ordersCount: number; orders: OrderItem[] },
        GetOrdersDTO
      >({
        providesTags: (_result, _error) => [{ type: 'Orders', id: 'LIST' }],
        query: DTO => ({
          method: 'POST',
          url: ENDPOINTS.MY_ORDERS_2_2,
          data: DTO
        }),
        transformResponse: (response: GetOrdersResponse) => {
          return mapGetOrdersResponse(response)
        }
      }),

      fetchOrdersHistoryList: build.query<
        { ordersCount: number; orders: OrderItem[] },
        GetOrdersHistoryDTO
      >({
        providesTags: (_result, _error) => [{ type: 'Orders', id: 'LIST' }],
        query: ({ status, ...DTO }) => ({
          method: 'POST',
          url: ENDPOINTS.MY_ORDERS_HISTORY,
          data: DTO
        }),
        transformResponse: (response: GetOrdersResponse, _, { status }) => {
          try {
            const ordersByStatus = status
              ? response.orders.filter(order => order.status.key === status)
              : response.orders.filter(order =>
                  HISTORY_STATUSES.includes(order.status.key)
                )

            return mapGetOrdersResponse({
              orders: ordersByStatus,
              orders_count: ordersByStatus.length
            })
          } catch (error) {
            console.error('Error in transformResponse:', error)
            throw new Error('Failed to process orders history response')
          }
        }
      }),

      fetchOrderDetails: build.query<
        ApiFetchOrderDetailsResponse,
        Parameters<typeof ENDPOINTS.ORDER_DETAILS_V_3>[0]
      >({
        providesTags: (_result, _error, { orderId }) => [
          { type: 'Orders', id: orderId }
        ],
        query: ({ storeId, orderId }) => ({
          method: 'GET',
          url: ENDPOINTS.ORDER_DETAILS_V_3({ storeId, orderId })
        }),
        transformResponse: (response: ApiFetchOrderDetailsResponse) => {
          return parseResponse(response, ApiFetchOrderDetailsResponseSchema)
        }
      }),

      fetchOrderBillings: build.query<
        ApiFetchOrderBillingsResponse,
        Parameters<typeof ENDPOINTS.ORDER_BILLINGS>[0]
      >({
        providesTags: (_result, _error, { orderId }) => [
          { type: 'Orders', id: orderId }
        ],
        query: ({ storeId, orderId }) => ({
          method: 'GET',
          url: ENDPOINTS.ORDER_BILLINGS({ storeId, orderId })
        }),
        transformResponse: (response: ApiFetchOrderBillingsResponse) => {
          return parseResponse(response, ApiFetchOrderBillingsResponseSchema)
        }
      }),

      acceptOrder: build.mutation<
        ApiAcceptOrder,
        { order_id: string; estimated_preparation_time: number }
      >({
        invalidatesTags: (_result, _error, { order_id }) => {
          if (_error) return []
          return [
            { type: 'Orders', id: 'LIST' },
            { type: 'Orders', id: order_id }
          ]
        },
        query: data => ({
          method: 'POST',
          url: ENDPOINTS.ACCEPT_ORDER,
          data
        }),
        transformResponse: (response: ApiAcceptOrder) => {
          return parseResponse(response, ApiAcceptOrderSchema)
        }
      }),

      setOrderReady: build.mutation<ApiBaseResponse, { order_id: string }>({
        invalidatesTags: (_result, _error, { order_id }) => [
          { type: 'Orders', id: 'LIST' },
          { type: 'Orders', id: order_id }
        ],
        query: data => ({
          method: 'POST',
          url: ENDPOINTS.READY_ORDER,
          data
        }),
        transformResponse: (response: ApiBaseResponse) => {
          return parseResponse(response, ApiBaseResponseSchema)
        }
      }),

      returnToStockOrder: build.mutation<
        ApiBaseResponse,
        Parameters<typeof ENDPOINTS.RETURN_TO_STOCK_ORDER>[0]
      >({
        invalidatesTags: (_result, _error, { order_id }) => [
          { type: 'Orders', id: 'LIST' },
          { type: 'Orders', id: order_id }
        ],
        query: ({ store_id, order_id }) => ({
          method: 'POST',
          url: ENDPOINTS.RETURN_TO_STOCK_ORDER({ store_id, order_id })
        }),
        transformResponse: (response: ApiBaseResponse) => {
          return parseResponse(response, ApiBaseResponseSchema)
        }
      }),

      rejectOrder: build.mutation<
        ApiBaseResponse,
        { reason: string; order_id: string }
      >({
        invalidatesTags: (_result, _error, { order_id }) => [
          { type: 'Orders', id: 'LIST' },
          { type: 'Orders', id: order_id }
        ],
        query: data => ({
          method: 'POST',
          url: ENDPOINTS.CANCEL_ORDER,
          data
        }),
        transformResponse: (response: ApiBaseResponse) => {
          return parseResponse(response, ApiBaseResponseSchema)
        }
      }),

      searchOrdersHistory: build.query<
        OrderItem[],
        Partial<{
          search: string
          category: 'ITEM' | 'ORDER_ID' | 'PERSON'
          isFilterOnly: boolean
          statuses: string[]
          start_date: string
          end_date: string
          start_price: number
          end_price: number
          brand: string
          skip: number
          limit: number
        }> & { store_id: string }
      >({
        providesTags: [{ type: 'Orders', id: 'LIST' }],
        query: data => ({
          method: 'POST',
          url: ENDPOINTS.HISTORY_SEARCH,
          data
        }),
        transformResponse: (response: ApiSearchOrdersHistoryResponse) => {
          // return parseResponse(response, ApiSearchOrdersHistoryResponseSchema)
          return (response.orders as unknown as OrderItemAPI[]).map(
            mapOrderItem
          )
        }
      }),

      getOrdersCountByTypes: build.query<
        {
          new: number
          inProgress: number
        },
        { store_id: string }
      >({
        providesTags: [{ type: 'Orders', id: 'LIST' }],
        queryFn: async function (_arg, _queryApi, _extraOptions, fetchWithBQ) {
          const fetchNew = fetchWithBQ({
            method: 'POST',
            url: ENDPOINTS.MY_ORDERS_2_2,
            data: { status: 'NEW_ORDER', store_id: _arg.store_id }
          })
          const fetchInProgress = fetchWithBQ({
            method: 'POST',
            url: ENDPOINTS.MY_ORDERS_2_2,
            data: { status: 'ONGOING', store_id: _arg.store_id }
          })
          const results = await Promise.allSettled([fetchNew, fetchInProgress])

          const counts = results.map((result, index) => {
            if (result.status === 'fulfilled') {
              const data = result.value.data
              if (
                typeof data === 'object' &&
                data !== null &&
                'orders_count' in data
              ) {
                if (index === 0) {
                  return (
                    data as {
                      orders_count: number
                      orders: { time_left: number }[]
                    }
                  )?.orders?.reduce(
                    (acc, order) => (order.time_left === 0 ? acc : acc + 1),
                    0
                  )
                } else {
                  return (data as { orders_count: number })?.orders_count
                }
              }
            }
            return 0
          })

          return {
            data: {
              new: counts[0],
              inProgress: counts[1]
            }
          }
        }
      }),

      fetchRejectionReasons: build.query<
        ApiFetchRejectionReasons,
        Parameters<typeof ENDPOINTS.REJECTION_REASONS>[0]
      >({
        query: ({ store_id }) => ({
          method: 'GET',
          url: ENDPOINTS.REJECTION_REASONS({ store_id })
        }),
        transformResponse: (response: ApiFetchRejectionReasons) => {
          return parseResponse(response, ApiFetchRejectionReasonsSchema)
        }
      })
    }
  }
})

export const {
  useLazyFetchOrderDetailsQuery,
  useLazyFetchOrderBillingsQuery,
  useAcceptOrderMutation,
  useSetOrderReadyMutation,
  useReturnToStockOrderMutation,
  useRejectOrderMutation,
  useLazySearchOrdersHistoryQuery,
  useGetOrdersCountByTypesQuery,
  useLazyGetOrdersCountByTypesQuery,
  useFetchRejectionReasonsQuery,
  useLazyFetchOrdersListQuery,
  useFetchOrdersListQuery,
  useLazyFetchOrdersHistoryListQuery
} = ordersApi
